import { Phone, MessageSquare } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { aboutImg } from "@/data/content";
import { useBusinessInfo } from "@/lib/useBusinessInfo";

export const About = () => {
  const { t } = useLanguage();
  const biz = useBusinessInfo();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="bg-ink py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid gap-14 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-7">
            <Reveal>
              <SectionLabel text={t.about.label} testId="about-label" />
              <h2
                data-testid="about-heading"
                className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-6xl"
              >
                {t.about.heading}
              </h2>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
                {t.about.text}
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300">
                {t.about.text2}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <span
                  data-testid="about-se-habla-badge"
                  className="rounded-full border border-gold/40 bg-gold/10 px-5 py-2 text-xs font-black uppercase tracking-[0.15em] text-gold"
                >
                  {t.about.seHabla}
                </span>
                <span className="rounded-full border border-white/15 px-5 py-2 text-xs font-black uppercase tracking-[0.15em] text-neutral-300">
                  EN · ES
                </span>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -left-3 -top-3 h-16 w-16 border-l-2 border-t-2 border-gold" />
                <div className="aspect-[4/3] overflow-hidden border border-white/10">
                  <img
                    src={aboutImg}
                    alt="Drywall finishing work by the R3Stan crew"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div
                  data-testid="about-contact-card"
                  className="relative z-10 -mt-14 ml-5 border border-white/10 bg-charcoal p-7 md:ml-10 md:p-9"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">
                    {t.about.role}
                  </p>
                  <p className="mt-2 font-display text-3xl font-black tracking-tight text-white">
                    {biz.contactName}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      data-testid="about-call-btn"
                      href={biz.phoneHref}
                      className="flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {t.about.callCta}
                    </a>
                    <a
                      data-testid="about-text-btn"
                      href={biz.smsHref}
                      className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                      {t.about.textCta}
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
